import { P as PubSub } from './pubsub-COBwjfLh.js';
import './notyf-DIw0RvBj.js';

const GRID_COLUMN_CLASS_MAP = {
  4: ['md:grid-cols-3', 'lg:grid-cols-4'],
  6: ['md:grid-cols-3', 'lg:grid-cols-6', 'cards--small'],
};
const ALL_GRID_COLUMN_CLASSES = Object.values(GRID_COLUMN_CLASS_MAP).flat();
const GRID_VIEW_PARAM = 'grid_view';
const GRID_VIEW_CHANGE_EVENT = 'grid-view:change';

/**
 * @param {string | null | undefined} value
 * @returns {'4' | '6' | null}
 */
function resolveGridViewParam(value) {
  if (value === '6' || value === '4') return value;
  return null;
}

function getGridViewFromUrl() {
  return resolveGridViewParam(new URLSearchParams(window.location.search).get(GRID_VIEW_PARAM));
}

/**
 * @param {'4' | '6'} columns
 */
function updateUrlGridView(columns) {
  const url = new URL(window.location.href);
  url.searchParams.set(GRID_VIEW_PARAM, columns);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}

class GridToggle extends HTMLElement {
  /** @type {HTMLButtonElement[]} */ #buttons = [];
  /** @type {(() => void) | null} */ #unsubscribe = null;
  /** @type {(() => void) | null} */ #gridViewUnsubscribe = null;
  /** @type {MutationObserver | null} */ #gridObserver = null;
  /** @type {string | null} */ #activeColumns = null;

  connectedCallback() {
    this.#buttons = [...this.querySelectorAll('[data-grid-columns]')];
    if (!this.#buttons.length) return;

    this.#buttons.forEach((button) => {
      button.addEventListener('click', () => this.#selectButton(button));
    });

    this.#applyDefaultColumns();
    this.#observeGridList();
    this.#listenForListingGridUpdates();
    this.#listenForGridViewChanges();
  }

  disconnectedCallback() {
    this.#unsubscribe?.();
    this.#gridViewUnsubscribe?.();
    this.#gridObserver?.disconnect();
  }

  #getDefaultColumns() {
    const columns = this.dataset.gridColumnsDefault ?? '4';
    return columns === '6' ? '6' : '4';
  }

  #getInitialColumns() {
    return getGridViewFromUrl() ?? this.#getDefaultColumns();
  }

  #applyDefaultColumns() {
    this.#setActiveColumns(this.#getInitialColumns());
    this.#applyColumns(this.#activeColumns);
  }

  /**
   * @param {string} columns
   */
  #setActiveColumns(columns) {
    const normalizedColumns = columns === '6' ? '6' : '4';
    this.#activeColumns = normalizedColumns;

    const activeButton =
      this.#buttons.find((button) => button.dataset.gridColumns === normalizedColumns) ??
      this.#buttons[0];

    this.#buttons.forEach((button) => {
      const isActive = button === activeButton;
      button.classList.toggle('active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getScopeElement() {
    const algoliaRoot = this.closest('algolia-collection, algolia-search');
    if (algoliaRoot) return algoliaRoot;

    return this.closest('[id^="shopify-section-"]')?.querySelector('listing-grid') ?? null;
  }

  /**
   * @param {HTMLButtonElement} button
   */
  #selectButton(button) {
    const columns = button.dataset.gridColumns;
    if (!columns || columns === this.#activeColumns) return;

    this.#setActiveColumns(columns);
    this.#applyColumns(columns);
    updateUrlGridView(columns === '6' ? '6' : '4');
    this.#publishGridViewChange(columns);
  }

  #publishGridViewChange(columns) {
    const scope = this.#getScopeElement();
    if (!scope) return;

    PubSub.publish(GRID_VIEW_CHANGE_EVENT, {
      data: {
        columns: columns === '6' ? '6' : '4',
        scope,
        source: this,
      },
    });
  }

  /**
   * @param {string} columns
   */
  #applyColumns(columns) {
    this.#activeColumns = columns;

    const scope = this.#getScopeElement();
    if (!scope) return;

    scope.dataset.gridColumns = columns;

    // Prefer the live InstantSearch list over the Liquid skeleton when both exist.
    const gridList =
      scope.tagName === 'LISTING-GRID'
        ? scope.querySelector('ol')
        : (scope.querySelector('.ais-Hits .ais-Hits-list') ??
          scope.querySelector('.ais-Hits-list'));

    if (!gridList) return;

    const columnClasses = GRID_COLUMN_CLASS_MAP[columns === '6' ? 6 : 4];
    gridList.classList.remove(...ALL_GRID_COLUMN_CLASSES);
    gridList.classList.add(...columnClasses);
  }

  /**
   * @param {HTMLElement} gridList
   * @param {string} columns
   */
  #needsColumnSync(gridList, columns) {
    const expected = GRID_COLUMN_CLASS_MAP[columns === '6' ? 6 : 4];
    return expected.some((className) => !gridList.classList.contains(className));
  }

  #observeGridList() {
    const scope = this.#getScopeElement();
    if (!scope || scope.tagName === 'LISTING-GRID') return;

    const sync = () => {
      if (!this.#activeColumns) return;

      const gridList =
        scope.querySelector('.ais-Hits .ais-Hits-list') ?? scope.querySelector('.ais-Hits-list');
      if (!gridList || !this.#needsColumnSync(gridList, this.#activeColumns)) return;

      this.#applyColumns(this.#activeColumns);
    };

    sync();

    // Keep watching: InstantSearch replaces the list after init and can wipe column classes.
    this.#gridObserver = new MutationObserver(sync);
    this.#gridObserver.observe(scope, { childList: true, subtree: true });
  }

  #listenForListingGridUpdates() {
    if (this.closest('algolia-collection, algolia-search')) return;

    this.#unsubscribe = PubSub.subscribe('collection:filter', ({ status }) => {
      if (status === 'success' && this.#activeColumns) {
        this.#applyColumns(this.#activeColumns);
      }
    });
  }

  #listenForGridViewChanges() {
    this.#gridViewUnsubscribe = PubSub.subscribe(GRID_VIEW_CHANGE_EVENT, ({ data }) => {
      if (!data?.scope || data.scope !== this.#getScopeElement()) return;
      if (data.source === this) return;

      this.#setActiveColumns(data.columns);
      this.#applyColumns(data.columns);
    });
  }
}

customElements.define('grid-toggle', GridToggle);
